import React, { useState } from 'react';
import axios from '../../api/axios';
import './GroupResultModal.css';

export default function GroupResultModal({ group, onClose, locale = 'ru' }) {
  const [loadingFileId, setLoadingFileId] = useState(null); // id файла или письма, который качается

  if (!group) return null;

  const t = {
    kz: {
      title: '🗂 Топ нәтижелері',
      name: 'Тақырып:',
      created: 'Құрылған күні:',
      outNum: 'Шығыс №:',
      apps: 'Өтінімдер',
      letters: 'Хаттар',
      appTitle: 'Атауы:',
      score: 'Бағасы:',
      downloadConclusion: 'Қорытындыны жүктеу',
      letterTheme: 'Тақырыбы:',
      downloadLetter: 'Хатты жүктеу',
      loading: 'Жүктелуде…',
      errConclusion: 'Қорытындыны жүктеу қатесі',
      errLetter: 'Хатты жүктеу қатесі',
    },
    ru: {
      title: '🗂 Результаты группы',
      name: 'Название:',
      created: 'Дата создания:',
      outNum: 'Исходящий №:',
      apps: 'Заявки',
      letters: 'Письма',
      appTitle: 'Название:',
      score: 'Оценка:',
      downloadConclusion: 'Скачать заключение',
      letterTheme: 'Тема:',
      downloadLetter: 'Скачать письмо',
      loading: 'Загрузка…',
      errConclusion: 'Ошибка при скачивании заключения',
      errLetter: 'Ошибка при скачивании письма',
    },
  }[locale];

  const onOverlayClick = (e) => {
    if (e.currentTarget === e.target) onClose?.();
  };

  const downloadWithLoader = async (fn, id) => {
    try {
      setLoadingFileId(id);
      await fn();
    } finally {
      setLoadingFileId(null);
    }
  };

  return (
    <div className="grm-overlay" onClick={onOverlayClick} role="dialog" aria-modal="true">
      <div className="grm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="grm-close" onClick={onClose} aria-label="Close">×</button>

        <h2 className="grm-title">{t.title}</h2>

        <div className="grm-summary">
          <p className="grm-row"><span className="grm-label">{t.name}</span><span className="grm-value">{group.title}</span></p>
          <p className="grm-row"><span className="grm-label">{t.created}</span><span className="grm-value">{new Date(group.created_at).toLocaleString()}</span></p>
          <p className="grm-row"><span className="grm-label">{t.outNum}</span><span className="grm-value">{group.outgoing_number || '-'}</span></p>
        </div>

        <h3 className="grm-section-title">📄 {t.apps}</h3>

        <div className="grm-cards">
          {group.applications.map((app) => (
            <div key={app.id} className="grm-card">
              <p className="grm-line"><strong>{t.appTitle}</strong> {app.title}</p>
              <p className="grm-line">
                <strong>
                  {app.scientist_conclusion
                    ? (locale === 'kz' ? 'Шешімі:' : 'Решение:')
                    : t.score}
                </strong>{' '}
                {app.scientist_conclusion
                  ? app.scientist_conclusion
                  : (app.final_document?.average_score ?? '-')}
              </p>

              {app.final_document?.file_path && (
                <button
                  className={`grm-btn grm-btn--primary ${loadingFileId === `app-${app.id}` ? 'is-loading' : ''}`}
                  disabled={loadingFileId === `app-${app.id}`}
                  onClick={() => downloadWithLoader(async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const response = await axios.post(
                        'https://expertise.gov.kz/api/publisher/final-documents/download',
                        { path: app.final_document.file_path },
                        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
                      );
                      const blob = new Blob([response.data], { type: response.headers['content-type'] });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', app.final_document.file_path.split('/').pop());
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      console.error('Download error:', error);
                      alert(t.errConclusion);
                    }
                  }, `app-${app.id}`)}
                >
                  {loadingFileId === `app-${app.id}` ? <span className="grm-spinner" /> : '📥'} 
                  {loadingFileId === `app-${app.id}` ? t.loading : t.downloadConclusion}
                </button>
              )}
            </div>
          ))}
        </div>

        <h3 className="grm-section-title">✉️ {t.letters}</h3>

        <div className="grm-cards">
          {group.letters.map((letter) => (
            <div key={letter.id} className="grm-card grm-card--letter">
              <p className="grm-line"><strong>{t.letterTheme}</strong> {letter.theme}</p>
              <button
                className={`grm-btn grm-btn--primary ${loadingFileId === `letter-${letter.id}` ? 'is-loading' : ''}`}
                disabled={loadingFileId === `letter-${letter.id}`}
                onClick={() => downloadWithLoader(async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await axios.get(
                      `https://expertise.gov.kz/api/publisher/download-letter/${letter.id}`,
                      { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }
                    );
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `letter_${letter.id}.docx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  } catch (error) {
                    console.error('Letter download error:', error);
                    alert(t.errLetter);
                  }
                }, `letter-${letter.id}`)}
              >
                {loadingFileId === `letter-${letter.id}` ? <span className="grm-spinner" /> : '📥'} 
                {loadingFileId === `letter-${letter.id}` ? t.loading : t.downloadLetter}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
